import { API_URL } from '../config/config';
import axios from 'axios';
import { axiosNoInterceptor } from './axiosInterceptor';
import {
    syncSessionToExtensionSilently,
    logoutExtensionSilently
} from './extensionSyncService';

// Export standalone functions for backwards compatibility
export const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000;
        // Si le token est expiré, considérer l'utilisateur comme non authentifié
        if (Date.now() >= expirationTime) {
            return false;
        }
        return true;
    } catch (error) {
        console.error('Error validating token:', error);
        return false;
    }
};

export const logout = () => {
    // Purger la session de l'extension avant d'effacer la nôtre.
    logoutExtensionSilently();
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userInfo');
};

export const login = async (email, password, rememberMe = true) => {
    const authService = new AuthService();
    return authService.login(email, password, rememberMe);
};

class AuthService {    
    async login(email, password, rememberMe = true) {
        // Le consentement cookies relève du seul bandeau (CookieConsent) : la
        // connexion ne doit jamais le forcer, sinon GA et l'attribution
        // tourneraient pour des utilisateurs ayant refusé.
        try {
            const response = await fetch(`${API_URL}/login_check`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });
            
            if (!response.ok) {
                console.error('Échec d\'authentification, status:', response.status);
                const errorText = await response.text();
                console.error('Détails de l\'erreur:', errorText);
                throw new Error(`Authentication failed (${response.status}): ${errorText}`);
            }

            const data = await response.json();
            this.setTokens(data.token, data.refresh_token);

            // Stockage des préférences de session
            localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false');

            // Stocker les informations utilisateur directement depuis la réponse JWT
            if (data.user) {
                localStorage.setItem('userInfo', JSON.stringify({
                    id: data.user.id,
                    email: data.user.email,
                    firstName: data.user.firstName,
                    lastName: data.user.lastName,
                    roles: data.user.roles,
                    subscription: data.user.subscription,
                    payment_required: data.user.payment_required,
                    isEmailVerified: data.user.isEmailVerified
                }));
            } else {
                // Fallback vers l'ancienne méthode si les données utilisateur ne sont pas dans la réponse JWT
                const userResponse = await fetch(`${API_URL}/user/current`, {
                    headers: {
                        'Authorization': `Bearer ${data.token}`,
                    },
                });

                if (userResponse.ok) {
                    const userData = await userResponse.json();
                    localStorage.setItem('userInfo', JSON.stringify({
                        email: userData.email,
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        roles: userData.roles || []
                    }));
                }
            }

            // Configuration du token dans les headers par défaut d'axios
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

            // Pousser la session à l'extension : elle devient utilisable sans ressaisie.
            syncSessionToExtensionSilently({
                token: data.token,
                refreshToken: data.refresh_token,
                email: (data.user && data.user.email) || email
            });

            // Déclencher un événement pour notifier les autres composants du changement
            window.dispatchEvent(new Event('localStorageUpdated'));
            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    getToken() {
        return localStorage.getItem('token');
    }

    getRefreshToken() {
        return localStorage.getItem('refresh_token');
    }    checkCookieConsent() {
        const consent = localStorage.getItem('cookieConsent');
        return consent === 'accepted';
    }

    setToken(token) {
        if (!this.checkCookieConsent()) {
            throw new Error('Cookie consent required');
        }
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    setTokens(token, refreshToken) {
        if (!this.checkCookieConsent()) {
            throw new Error('Cookie consent required');
        }
        localStorage.setItem('token', token);
        if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    isAuthenticated() {
        const token = this.getToken();
        if (!token) return false;
        
        return !this.isTokenExpired(token);
    }

    logout() {
        // Purger la session de l'extension avant d'effacer la nôtre.
        logoutExtensionSilently();
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('rememberMe');
        delete axios.defaults.headers.common['Authorization'];
    }

    isTokenExpired(token) {
        if (!token) return true;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const expirationTime = payload.exp * 1000;
            const isExpired = Date.now() >= expirationTime;
            
            // Log seulement en développement
            if (process.env.REACT_APP_ENV === 'development') {
                console.log('Token expiration check:', {
                    now: new Date(Date.now()).toISOString(),
                    expiration: new Date(expirationTime).toISOString(),
                    isExpired: isExpired
                });
            }
            
            return isExpired;
        } catch (error) {
            console.error('Error checking token expiration:', error);
            return true;
        }
    }

    async refreshToken() {
        try {
            const refreshToken = this.getRefreshToken();
            const currentToken = this.getToken();
            
            if (!refreshToken) {
                console.warn('Token refresh skipped: No refresh_token in localStorage');
                this.logout();
                throw new Error('No refresh token available');
            }
            
            // Utiliser l'instance axios sans intercepteur pour éviter les boucles infinies
            const response = await axiosNoInterceptor.post(`${API_URL}/token/refresh`, {
                refresh_token: refreshToken
            });
            
            if (response.data.token) {
                this.setToken(response.data.token);
                
                // Si un nouveau refresh_token est fourni, le stocker également
                if (response.data.refresh_token) {
                    localStorage.setItem('refresh_token', response.data.refresh_token);
                }

                // Le refresh_token peut avoir été renouvelé : repousser le couple à
                // l'extension pour qu'elle ne conserve pas un jeton devenu invalide.
                syncSessionToExtensionSilently({ token: response.data.token });

                return response.data.token;
            }
            throw new Error('No token in refresh response');
        } catch (error) {
            console.error('Token refresh failed:', error);
            this.logout();
            throw error;
        }
    }
    
    restoreSession() {
        const token = this.getToken();
        if (token && !this.isTokenExpired(token)) {
            // Si le token est valide, configurer le header d'autorisation
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return true;
        } else if (token && this.isTokenExpired(token)) {
            // Si le token est expiré mais présent, tenter de le rafraîchir
            this.refreshToken().catch(() => {
                this.logout();
            });
            return true;
        }
        return false;
    }
}

export default new AuthService();
